import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  SelectChangeEvent,
  IconButton,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
  Link,
  Tooltip as MuiTooltip,
  Stack,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { Add as AddIcon, Sync as SyncIcon, Delete as DeleteIcon, PhotoCamera as PhotoCameraIcon, PhotoLibrary as PhotoLibraryIcon, ExpandMore as ExpandMoreIcon, SwapHoriz as PromoteIcon, AddAPhoto as AddAPhotoIcon, OpenInNew as OpenInNewIcon, Edit as EditIcon, DriveFileMove as MoveIcon, AccountBalanceWallet as InvestorLinkIcon } from '@mui/icons-material';
import { Project } from '../types/Project';
import dataService from '../services/dataService';
import { resolveBudgetsForProjects } from '../utils/calcsheetBudget';
import { useQuotationStore } from '../store/quotationStore';
import { PURCHASE_ORDERS_STORAGE_KEY, type PurchaseOrder, type PurchaseOrderItem } from './PurchaseOrderPage';
import { API_BASE } from '../config/api';
import { PROJECT_EXPENSE_CATEGORIES, OVERHEAD_CATEGORIES, INVOICE_TYPES, INVESTORS, type FundingSource } from '../data/financeCategories';
import { parseReceipt } from '../services/receiptParseService';
import {
  checkDuplicates,
  computeImageHash,
  describeMatch,
  type DuplicateMatch,
} from '../services/receiptDuplicateService';
import { blobToBase64, compressForUpload } from '../utils/receipts/imageCompress';
import {
  getReceiptParseMimeType,
  isPdfReceipt,
  makeReceiptUploadFilename,
  RECEIPT_FILE_ACCEPT,
  validateReceiptFile,
} from '../utils/receipts/receiptFile';
import { suggestReceiptCrop } from '../services/receiptCropService';
import { perspectiveCropToBlob, type Quad } from '../utils/receipts/perspectiveCrop';
import ReceiptCropper from './ReceiptCropper';
import ReceiptViewer from './ReceiptViewer';
import ScanBatch from './ScanBatch';
import ScanWithPhoneButton from './ScanWithPhoneButton';
import { useAuth } from '../contexts/AuthContext';
import RecurringExpensesTray from './finance/RecurringExpensesTray';
import { getDriveItemThumbnailUrl, fetchDriveItemBlob, deleteDriveItem, replaceDriveItemContent } from '../services/onedriveFolderService';
import MoneyTrailButton from './finance/MoneyTrailButton';
import { useFinanceRowFocus } from '../hooks/useFinanceRowFocus';
import { financeFocusToken, financeFocusUrl } from '../utils/financeTraceFocus';
import { expenseOrigin, linkedOriginsForExpense } from '../utils/expenseFinanceTrace';
import type { ParsedReceipt } from '../types/Receipt';

const EXPENSES_KEY = 'projectExpenses';

async function convertHeicToJpeg(file: File): Promise<File> {
  const name = file.name.toLowerCase();
  const isHeic = name.endsWith('.heic') || name.endsWith('.heif') || file.type === 'image/heic' || file.type === 'image/heif';
  if (!isHeic) return file;
  try {
    const heic2any = (await import('heic2any')).default;
    const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const blob = Array.isArray(result) ? result[0] : result;
    return new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

export type ExpenseScope = 'project' | 'overhead';

// Sentinel value for the Project filter/select so "Overhead" can live in the same
// dropdown as real projects instead of a second control (roadmap §3).
const OVERHEAD_SENTINEL = '__overhead__';
// Every project expense, no overhead rows — the complement of OVERHEAD_SENTINEL.
const ALL_PROJECTS_SENTINEL = '__all_projects__';

export interface ProjectExpense {
  id: string;
  /** Which Firestore collection this row actually lives in — project_expenses or overhead_expenses.
   *  Client-side tag only, set when fetched; not a stored field. */
  scope: ExpenseScope;
  // Only present for scope === 'project' rows.
  projectId?: string;
  projectName?: string;
  description: string;
  /** Free-text note on what the expense was for, e.g. "lunch out with client" */
  remarks?: string;
  amount: number;
  date: string;
  category: string;
  createdAt: string;
  createdBy?: string;
  /** When synced from PO, avoid duplicate sync */
  sourcePoId?: string;
  sourcePoItemId?: string;
  /** When synced from Liquidation, avoid duplicate sync */
  sourceLiquidationId?: string;
  sourceLiquidationRowId?: string;
  /** Denormalized from the liquidation at sync time — GET /api/liquidations is
   *  scoped to the requesting user's own filings for non-admins, so a client-side
   *  join wouldn't resolve for other employees' liquidation-sourced rows. */
  liquidationFiledBy?: string;
  liquidationFiledAt?: string;
  sourceType?: 'manual' | 'receipt_scan' | 'po_sync' | 'liquidation_sync' | 'migrated' | 'recurring';
  sourceCaId?: string;
  supplier?: string;
  invoiceNo?: string;
  invoiceType?: string;
  vat?: number;
  tin?: string;
  fundingSource?: FundingSource;
  receiptRef?: { oneDriveId: string; webUrl: string; filename: string };
  /** Income-tax-deductible business expense — AI-suggested on scan, editable by accounting. */
  deductible?: boolean | null;
}

const loadExpenses = (): ProjectExpense[] => {
  try {
    const raw = localStorage.getItem(EXPENSES_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return [];
};

const loadPOs = (): PurchaseOrder[] => {
  try {
    const raw = localStorage.getItem(PURCHASE_ORDERS_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return [];
};

const lineTotal = (item: PurchaseOrderItem): number =>
  (Number(item.quantity) || 0) * (Number(item.unitPrice ?? 0) || 0);

const NET_PACIFIC_COLORS = {
  primary: '#2c5aa0',
  secondary: '#1e4a72',
  accent1: '#4f7bc8',
  accent2: '#3c6ba5',
  success: '#00b894',
  warning: '#fdcb6e',
  error: '#e84393',
  info: '#74b9ff',
};

interface ExpenseCategory {
  id: string;
  name: string;
  color: string;
  budget: number;
  spent: number;
}

interface MonthlyExpense {
  month: string;
  total: number;
  categories: Record<string, number>;
}

const PIE_COLORS = [
  NET_PACIFIC_COLORS.primary,
  NET_PACIFIC_COLORS.success,
  NET_PACIFIC_COLORS.warning,
  NET_PACIFIC_COLORS.info,
  NET_PACIFIC_COLORS.error,
  NET_PACIFIC_COLORS.accent1,
  NET_PACIFIC_COLORS.accent2,
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR + 2 - 2025 }, (_, i) => 2025 + i);

const ExpenseMonitoring: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Mounted under both /expense-monitoring and /finance/expense-monitoring — match by suffix
  const isChildRoute = /\/(ca-form|liquidation-form|direct-labor)$/.test(location.pathname);
  
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const csProjects = useQuotationStore((s) => s.projects);
  const quotations = useQuotationStore((s) => s.quotations);
  const [selectedYear, setSelectedYear] = useState<number>(0);
  // Month (1-12) and quarter (1-4) are mutually exclusive; 0 = no filter.
  const [selectedMonth, setSelectedMonth] = useState<number>(0);
  const [selectedQuarter, setSelectedQuarter] = useState<number>(0);
  const [selectedProjectId, setSelectedProjectId] = useState<string | ''>('');
  const [sortKey, setSortKey] = useState<'date' | 'project' | 'category' | 'description' | 'amount'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  // Portrait phones (< 600 px): expenses show as cards and the form dialogs go full-screen.
  const isPhone = useMediaQuery('(max-width:599.95px)');
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [budgets, setBudgets] = useState<Record<string, number>>({});
  const [expenses, setExpenses] = useState<ProjectExpense[]>([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [expenseScope, setExpenseScope] = useState<ExpenseScope>('project');
  const [expenseProjectId, setExpenseProjectId] = useState<string | ''>('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expenseRemarks, setExpenseRemarks] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('');
  const [expenseSupplier, setExpenseSupplier] = useState('');
  const [expenseInvoiceNo, setExpenseInvoiceNo] = useState('');
  const [expenseInvoiceType, setExpenseInvoiceType] = useState('');
  const [expenseVat, setExpenseVat] = useState('');
  const [expenseTin, setExpenseTin] = useState('');
  const [expenseFundingType, setExpenseFundingType] = useState<'corporate_bank' | 'corporate_card' | 'investor_outofpocket'>('corporate_bank');
  const [expenseFundingInvestor, setExpenseFundingInvestor] = useState('');
  const [expenseLinkedInvestments, setExpenseLinkedInvestments] = useState<{ id: string; date: string; category: string; description: string; amount: number }[]>([]);
  const [expenseLinkedInvestmentId, setExpenseLinkedInvestmentId] = useState('');
  // AI-suggested on scan (mirrors LiquidationFormPage); reviewed/corrected later in the Tax Ledger.
  const [expenseDeductible, setExpenseDeductible] = useState<boolean | null>(null);
  const [expenseDeductibleReason, setExpenseDeductibleReason] = useState<string | null>(null);
  // Duplicate-receipt detection: set after a scan parses successfully, cleared
  // on reset/retake/new scan. Non-blocking — a warning never prevents saving.
  const [expenseImageHash, setExpenseImageHash] = useState<string | null>(null);
  const [expenseDuplicateWarnings, setExpenseDuplicateWarnings] = useState<string[]>([]);
  // Guards against a cancelled/retaken scan's in-flight duplicate check repopulating
  // state after a newer scan has already started (or been cleared).
  const scanGenRef = useRef(0);
  const financeRowRefs = useRef(new Map<string, HTMLElement>());

  // Receipt crop step (mirrors ScanPage.tsx) before the AI parse.
  const [editUrl, setEditUrl] = useState<string | null>(null);
  const [editBlob, setEditBlob] = useState<Blob | null>(null);
  const [editQuad, setEditQuad] = useState<Quad | null>(null);

  // Superadmin: promote a manual/scanned expense to an employee's liquidation claim.
  const [promoteExpense, setPromoteExpense] = useState<ProjectExpense | null>(null);
  const [promoteUsers, setPromoteUsers] = useState<{ id: string; full_name: string | null; username: string }[]>([]);
  const [promoteUserId, setPromoteUserId] = useState('');
  const [promoteCAs, setPromoteCAs] = useState<{ id: string; ca_no: string; balance_remaining: number }[]>([]);
  const [promoteCaId, setPromoteCaId] = useState('');
  const [promoteDrafts, setPromoteDrafts] = useState<{ id: string; form_no: string; total_amount: number; status: string }[]>([]);
  const [promoteTargetLiquidationId, setPromoteTargetLiquidationId] = useState('');
  const [promoting, setPromoting] = useState(false);
  const [promoteError, setPromoteError] = useState('');
  const scanInputRef = useRef<HTMLInputElement>(null);
  const pendingReceiptRef = useRef<File | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [savingExpense, setSavingExpense] = useState(false);
  // Attach-receipt-later: retrofit a receipt photo onto an already-saved expense row
  // (no re-parse — the row's fields are already set, this just needs the image).
  const attachReceiptInputRef = useRef<HTMLInputElement>(null);
  const attachReceiptTargetRef = useRef<ProjectExpense | null>(null);
  const [attachingReceiptId, setAttachingReceiptId] = useState<string | null>(null);
  // Lazy OneDrive receipt thumbnails, keyed by oneDriveId (ported from OverheadExpensesPage).
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  // Floating receipt viewer pane: full image fetched through the OneDrive proxy.
  const [viewer, setViewer] = useState<{ expense: ProjectExpense; url: string | null } | null>(null);
  // Edit an existing row (scope-aware PATCH).
  const [editExpense, setEditExpense] = useState<ProjectExpense | null>(null);
  const [editFields, setEditFields] = useState({ description: '', remarks: '', amount: '', date: '', category: '', supplier: '', invoiceNo: '', invoiceType: '', vat: '', tin: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [savingDeductibleId, setSavingDeductibleId] = useState<string | null>(null);
  const [editError, setEditError] = useState('');
  // Move a row between overhead and a project (server-side copy+delete).
  const [moveExpense, setMoveExpense] = useState<ProjectExpense | null>(null);
  const [moveProjectId, setMoveProjectId] = useState('');
  const [moveCategory, setMoveCategory] = useState('');
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState('');
  const [scanSnackbar, setScanSnackbar] = useState<{ open: boolean; severity: 'success' | 'error' | 'warning'; message: string }>({ open: false, severity: 'success', message: '' });
  const [scanBatchOpen, setScanBatchOpen] = useState(false);
  const [expensesDialogProject, setExpensesDialogProject] = useState<Project | null>(null);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'info' | 'error'; text: string } | null>(null);
  const [migrating, setMigrating] = useState(false);
  const [localStorageCount] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(EXPENSES_KEY);
      if (!raw) return 0;
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.length : 0;
    } catch (_) { return 0; }
  });

  const fetchExpenses = useCallback(async () => {
    setExpensesLoading(true);
    try {
      const token = localStorage.getItem('netpacific_token');
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      const [projRes, overheadRes] = await Promise.all([
        fetch(`${API_BASE}/api/project-expenses`, { headers }),
        fetch(`${API_BASE}/api/overhead-expenses`, { headers }),
      ]);
      const projData = await projRes.json().catch(() => ({ success: false, expenses: [] }));
      const overheadData = await overheadRes.json().catch(() => ({ success: false, expenses: [] }));
      const projRows: ProjectExpense[] = (projData.expenses || []).map((e: Omit<ProjectExpense, 'scope'>) => ({ ...e, scope: 'project' as const }));
      const overheadRows: ProjectExpense[] = (overheadData.expenses || []).map((e: Omit<ProjectExpense, 'scope'>) => ({ ...e, scope: 'overhead' as const }));
      const merged = [...projRows, ...overheadRows].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setExpenses(merged);
    } catch {
      setExpenses([]);
    } finally {
      setExpensesLoading(false);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await dataService.getProjects().then(setAllProjects);
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  useEffect(() => {
    setBudgets(resolveBudgetsForProjects(allProjects, csProjects, quotations));
  }, [allProjects, csProjects, quotations]);

  // Reload expenses when navigating within expense monitoring (e.g. back from Liquidation form) so liquidations are reflected
  useEffect(() => {
    if (location.pathname.includes('/expense-monitoring')) {
      fetchExpenses();
    }
  }, [location.pathname, fetchExpenses]);

  const expensesInYear = useMemo(() => {
    if (selectedYear === 0 && selectedMonth === 0 && selectedQuarter === 0) return expenses;
    return expenses.filter((e) => {
      if (!e.date) return false;
      if (selectedYear !== 0 && !e.date.startsWith(String(selectedYear))) return false;
      const month = Number(e.date.slice(5, 7));
      if (selectedMonth !== 0 && month !== selectedMonth) return false;
      if (selectedQuarter !== 0 && Math.ceil(month / 3) !== selectedQuarter) return false;
      return true;
    });
  }, [expenses, selectedYear, selectedMonth, selectedQuarter]);

  // The budget cards/charts below are project-budget concepts — overhead expenses have
  // no project budget to track against, so they're excluded here (they still appear in
  // the unified Recent Expenses table below via `tableRows`).
  const projectExpensesInYear = useMemo(() => expensesInYear.filter((e) => e.scope === 'project'), [expensesInYear]);

  const spentByProject = useMemo(() => {
    const map: Record<string, number> = {};
    projectExpensesInYear.forEach((e) => {
      const key = String(e.projectId);
      map[key] = (map[key] || 0) + e.amount;
    });
    return map;
  }, [projectExpensesInYear]);

  const projectsForView = useMemo(() => {
    if (selectedProjectId === '' || selectedProjectId === ALL_PROJECTS_SENTINEL) return allProjects;
    const p = allProjects.find((x) => String(x.id) === selectedProjectId);
    return p ? [p] : [];
  }, [allProjects, selectedProjectId]);

  const expenseCategories = useMemo<ExpenseCategory[]>(() => {
    return projectsForView.map((p, i) => ({
      id: String(p.id),
      name: p.project_name,
      color: PIE_COLORS[i % PIE_COLORS.length],
      budget: budgets[String(p.id)] ?? 0,
      spent: spentByProject[String(p.id)] ?? 0,
    }));
  }, [projectsForView, budgets, spentByProject]);

  const expenseMetrics = useMemo(() => {
    // The all-projects sentinel behaves exactly like '' for the budget cards —
    // budgets are a project-only concept, so both aggregate every project.
    const isAggregate = selectedProjectId === '' || selectedProjectId === ALL_PROJECTS_SENTINEL;
    const totalBudget = isAggregate
      ? Object.values(budgets).reduce((a, b) => a + b, 0)
      : (budgets[selectedProjectId] ?? 0);
    const totalSpent = isAggregate
      ? projectExpensesInYear.reduce((sum, e) => sum + e.amount, 0)
      : selectedProjectId === OVERHEAD_SENTINEL
        ? expensesInYear.reduce((sum, e) => sum + (e.scope === 'overhead' ? e.amount : 0), 0)
        : (spentByProject[selectedProjectId] ?? 0);
    const totalRemaining = totalBudget - totalSpent;
    const spentPercentage = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;
    const overBudgetCount = isAggregate
      ? allProjects.filter((p) => (budgets[String(p.id)] ?? 0) > 0 && (spentByProject[String(p.id)] ?? 0) > (budgets[String(p.id)] ?? 0)).length
      : (totalBudget > 0 && totalSpent > totalBudget ? 1 : 0);
    const remainingPct = totalBudget > 0 ? (totalRemaining / totalBudget) * 100 : 100;
    const statusColor = totalRemaining < 0 ? 'red' : remainingPct <= 20 ? 'yellow' : 'green';
    return {
      totalBudget,
      totalSpent,
      totalRemaining,
      spentPercentage,
      overBudgetCategories: overBudgetCount,
      statusColor,
    };
  }, [budgets, expensesInYear, projectExpensesInYear, spentByProject, allProjects, selectedProjectId]);

  const pieChartData = expenseCategories.filter(cat => cat.spent > 0).map((cat, i) => ({
    name: cat.name.length > 12 ? cat.name.slice(0, 12) + '…' : cat.name,
    value: cat.spent,
    color: cat.color
  }));

  const budgetVsActualData = expenseCategories
    .filter(cat => cat.budget > 0 || cat.spent > 0)
    .slice(0, 10)
    .map(cat => ({
      category: cat.name.length > 8 ? cat.name.slice(0, 8) + '…' : cat.name,
      budget: cat.budget,
      spent: cat.spent,
      remaining: cat.budget - cat.spent
    }));

  const monthlyExpenseData = useMemo((): MonthlyExpense[] => {
    const byMonth: Record<string, number> = {};
    projectExpensesInYear.forEach((e) => {
      if (selectedProjectId !== '' && selectedProjectId !== ALL_PROJECTS_SENTINEL && String(e.projectId) !== selectedProjectId) return;
      const month = e.date ? e.date.slice(0, 7) : '';
      if (month) byMonth[month] = (byMonth[month] || 0) + e.amount;
    });
    return Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, total]) => ({ month, total, categories: {} }));
  }, [projectExpensesInYear, selectedProjectId]);

  // Unified table rows respecting the scope filter (roadmap §3): a specific project id
  // shows only that project's expenses, OVERHEAD_SENTINEL shows only overhead rows, and
  // '' (All) shows everything — both scopes, all projects.
  const tableRows = useMemo(() => {
    let rows: ProjectExpense[];
    if (selectedProjectId === OVERHEAD_SENTINEL) rows = expensesInYear.filter((e) => e.scope === 'overhead');
    else if (selectedProjectId === ALL_PROJECTS_SENTINEL) rows = expensesInYear.filter((e) => e.scope === 'project');
    else if (selectedProjectId !== '') rows = expensesInYear.filter((e) => e.scope === 'project' && String(e.projectId) === selectedProjectId);
    else rows = expensesInYear;

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'amount': cmp = a.amount - b.amount; break;
        case 'project': cmp = (a.projectName || '').localeCompare(b.projectName || ''); break;
        case 'category': cmp = (a.category || '').localeCompare(b.category || ''); break;
        case 'description': cmp = (a.description || '').localeCompare(b.description || ''); break;
        case 'date':
        default: cmp = String(a.date).localeCompare(String(b.date)); break;
      }
      return cmp * dir;
    });
  }, [expensesInYear, selectedProjectId, sortKey, sortDir]);

  // Reset to the first page whenever the filtered/sorted row set changes shape,
  // so the user never lands on an out-of-range empty page after changing filters.
  useEffect(() => {
    setPage(0);
  }, [selectedYear, selectedMonth, selectedQuarter, selectedProjectId, sortKey, sortDir]);

  const revealFocusedExpense = useCallback((expense: ProjectExpense) => {
    const year = Number(String(expense.date || '').slice(0, 4));
    setSelectedYear(Number.isFinite(year) ? year : 0);
    setSelectedMonth(0);
    setSelectedQuarter(0);
    setSelectedProjectId(
      expense.scope === 'overhead'
        ? OVERHEAD_SENTINEL
        : String(expense.projectId || ALL_PROJECTS_SENTINEL),
    );
  }, []);

  const focusedExpenseIndex = useCallback((expense: ProjectExpense) => (
    selectedMonth !== 0
    || selectedQuarter !== 0
    || selectedYear !== Number(String(expense.date || '').slice(0, 4))
    || selectedProjectId !== (
      expense.scope === 'overhead'
        ? OVERHEAD_SENTINEL
        : String(expense.projectId || ALL_PROJECTS_SENTINEL)
    )
      ? -1
      : tableRows.findIndex((row) => row.id === expense.id && row.scope === expense.scope)
  ), [selectedMonth, selectedProjectId, selectedQuarter, selectedYear, tableRows]);

  const financeFocus = useFinanceRowFocus({
    records: expenses,
    originForRecord: expenseOrigin,
    loading: expensesLoading,
    pageSize: rowsPerPage,
    setPage,
    revealRecord: revealFocusedExpense,
    indexForRecord: focusedExpenseIndex,
    rowRefs: financeRowRefs,
  });

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'amount' || key === 'date' ? 'desc' : 'asc'); }
  };

  const sortLabel = (key: typeof sortKey, text: string, align?: 'right') => (
    <TableSortLabel
      active={sortKey === key}
      direction={sortKey === key ? sortDir : 'asc'}
      onClick={() => handleSort(key)}
      sx={align === 'right' ? { flexDirection: 'row-reverse' } : undefined}
    >
      {text}
    </TableSortLabel>
  );

  const expensesForProject = (projectId: string) => expenses.filter((e) => e.scope === 'project' && String(e.projectId) === projectId);

  const handleSyncFromPO = async () => {
    const pos = loadPOs();
    const newExpenses: ProjectExpense[] = [];
    const isMultiMRF = (po: { mrfIds?: string[]; mrfRequestNos?: string[] }) =>
      (po.mrfIds && po.mrfIds.length > 1) || (po.mrfRequestNos && po.mrfRequestNos.length > 1);

    for (const po of pos) {
      const pid = po.projectId;
      if (pid == null) continue;

      const project = allProjects.find((p) => String(p.id) === String(pid));
      const projectName = project?.project_name ?? po.projectName ?? '—';
      const orderDate = po.orderDate || new Date().toISOString().slice(0, 10);

      if (isMultiMRF(po)) {
        // Multiple MRFs: log each item separately
        for (const item of po.items || []) {
          const amt = lineTotal(item);
          if (amt <= 0) continue;

          const mrfRequestNo = (() => {
            if (po.mrfIds && po.mrfRequestNos && item.id) {
              const idx = po.mrfIds.findIndex((id) => item.id.startsWith(`${id}-`));
              return idx >= 0 ? po.mrfRequestNos[idx] : po.mrfRequestNos[0];
            }
            return po.mrfRequestNo;
          })();
          const desc = [item.description, item.partNo, item.brand].filter(Boolean).join(' ') || '—';

          newExpenses.push({
            id: `exp-po-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            scope: 'project',
            projectId: String(pid),
            projectName,
            description: `PO ${po.poNumber} · MRF ${mrfRequestNo}: ${desc}`,
            amount: amt,
            date: orderDate,
            category: 'Materials',
            createdAt: new Date().toISOString(),
            sourcePoId: po.id,
            sourcePoItemId: item.id,
            sourceType: 'po_sync',
          });
        }
      } else {
        // Single MRF: one expense per PO (total)
        const total = (po.items || []).reduce((sum, item) => sum + lineTotal(item), 0);
        if (total <= 0) continue;

        newExpenses.push({
          id: `exp-po-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          scope: 'project',
          projectId: String(pid),
          projectName,
          description: `PO ${po.poNumber} (${po.supplierName || '—'})`,
          amount: total,
          date: orderDate,
          category: 'Materials',
          createdAt: new Date().toISOString(),
          sourcePoId: po.id,
          sourceType: 'po_sync',
        });
      }
    }

    if (newExpenses.length === 0) {
      setSyncMessage({ type: 'info', text: 'No new POs with prices to sync. All POs are already logged or have no item prices.' });
      setTimeout(() => setSyncMessage(null), 4000);
      return;
    }

    try {
      const token = localStorage.getItem('netpacific_token');
      const res = await fetch(`${API_BASE}/api/project-expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ expenses: newExpenses }),
      });
      const data = await res.json().catch(() => ({ success: false }));
      if (data.success) {
        const count = data.count ?? newExpenses.length;
        if (count === 0) {
          setSyncMessage({ type: 'info', text: 'No new POs with prices to sync. All POs are already logged or have no item prices.' });
        } else {
          setSyncMessage({ type: 'success', text: `Synced ${count} expense(s) from PO.` });
        }
        await fetchExpenses();
      } else {
        setSyncMessage({ type: 'error', text: 'Failed to sync PO expenses.' });
      }
    } catch {
      setSyncMessage({ type: 'error', text: 'Error syncing PO expenses.' });
    }
    setTimeout(() => setSyncMessage(null), 4000);
  };

  const handleSyncFromLiquidation = async () => {
    try {
      const token = localStorage.getItem('netpacific_token');
      if (!token) {
        setSyncMessage({ type: 'error', text: 'Not authenticated. Please log in first.' });
        setTimeout(() => setSyncMessage(null), 4000);
        return;
      }
      const res = await fetch(`${API_BASE}/api/liquidations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({ success: false }));
      if (!data.success || !data.liquidations) {
        setSyncMessage({ type: 'error', text: 'Failed to fetch liquidations.' });
        setTimeout(() => setSyncMessage(null), 4000);
        return;
      }

      const submitted = data.liquidations.filter((l: any) => l.status === 'submitted');
      const newExpenses: ProjectExpense[] = [];
      const newOverheadExpenses: ProjectExpense[] = [];
      for (const liq of submitted) {
        let rows: any[] = [];
        try { rows = JSON.parse(liq.rows_json || '[]'); } catch (_) { continue; }
        let receipts: any[] = [];
        try { receipts = JSON.parse(liq.receipts_json || '[]'); } catch (_) { receipts = []; }
        const receiptByRowId = new Map<string, { oneDriveId: string; webUrl: string; filename: string }>();
        receipts.forEach((r) => {
          if (r?.rowId && r?.oneDriveId && r?.webUrl && !receiptByRowId.has(r.rowId)) {
            receiptByRowId.set(r.rowId, { oneDriveId: r.oneDriveId, webUrl: r.webUrl, filename: r.filename || 'receipt' });
          }
        });

        for (const row of rows) {
          const pid = row.projectId != null && row.projectId !== '' ? String(row.projectId) : '';
          const amt = Number(row.amount);
          if (!amt || amt <= 0) continue;
          const base = {
            id: `exp-liq-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            description: `Liquidation ${liq.form_no}: ${(row.particulars || '').trim() || 'Liquidation'}`,
            remarks: (row.remarks || '').trim() || undefined,
            amount: amt,
            date: row.date || liq.date_of_submission || new Date().toISOString().slice(0, 10),
            category: (row.category || '').trim() || 'Others',
            createdAt: new Date().toISOString(),
            sourceLiquidationId: liq.id,
            sourceLiquidationRowId: row.id,
            sourceType: 'liquidation_sync' as const,
            ...(liq.employee_name ? { liquidationFiledBy: liq.employee_name } : {}),
            ...(liq.date_of_submission ? { liquidationFiledAt: liq.date_of_submission } : {}),
            ...(receiptByRowId.get(row.id) ? { receiptRef: receiptByRowId.get(row.id) } : {}),
          };
          if (pid) {
            const project = allProjects.find((p) => String(p.id) === pid);
            const projectName = row.projectName || project?.project_name || '—';
            newExpenses.push({ ...base, scope: 'project', projectId: pid, projectName });
          } else {
            // No project assigned — this is an overhead cost (e.g. a software
            // subscription liquidated without a project), not a dropped row.
            newOverheadExpenses.push({ ...base, scope: 'overhead' });
          }
        }
      }

      let syncedCount = 0;
      let syncFailed = false;
      if (newExpenses.length > 0) {
        const syncRes = await fetch(`${API_BASE}/api/project-expenses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ expenses: newExpenses }),
        });
        const syncData = await syncRes.json().catch(() => ({ success: false }));
        if (syncData.success) {
          syncedCount = syncData.count ?? newExpenses.length;
        } else {
          syncFailed = true;
        }
      }
      let overheadSyncedCount = 0;
      if (newOverheadExpenses.length > 0) {
        const overheadRes = await fetch(`${API_BASE}/api/overhead-expenses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ expenses: newOverheadExpenses }),
        });
        const overheadData = await overheadRes.json().catch(() => ({ success: false }));
        if (overheadData.success) {
          overheadSyncedCount = overheadData.count ?? newOverheadExpenses.length;
        } else {
          syncFailed = true;
        }
      }

      // Also backfill receiptRef onto already-synced liquidation expenses that
      // predate this passthrough (older filings whose scan was never linked).
      let backfilledCount = 0;
      try {
        const backfillRes = await fetch(`${API_BASE}/api/project-expenses/backfill-liquidation-receipts`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        const backfillData = await backfillRes.json().catch(() => ({ success: false }));
        if (backfillData.success) backfilledCount = backfillData.count ?? 0;
      } catch {
        // best-effort — sync itself already succeeded/failed independently
      }

      if (syncFailed) {
        setSyncMessage({ type: 'error', text: 'Failed to save liquidation expenses.' });
      } else if (syncedCount === 0 && overheadSyncedCount === 0 && backfilledCount === 0) {
        setSyncMessage({ type: 'info', text: 'No new liquidation expenses to sync. All submitted liquidations are already logged.' });
      } else {
        const parts = [];
        if (syncedCount > 0) parts.push(`Synced ${syncedCount} project expense(s)`);
        if (overheadSyncedCount > 0) parts.push(`${overheadSyncedCount} overhead expense(s) (no project assigned)`);
        if (backfilledCount > 0) parts.push(`linked ${backfilledCount} scanned receipt(s) to existing entries`);
        setSyncMessage({ type: 'success', text: `${parts.join('; ')}.` });
      }
      if (syncedCount > 0 || overheadSyncedCount > 0 || backfilledCount > 0) await fetchExpenses();
    } catch (err) {
      setSyncMessage({ type: 'error', text: 'Error syncing liquidations.' });
    }
    setTimeout(() => setSyncMessage(null), 4000);
  };

  const handleMigrateToCloud = async () => {
    const localExpenses = loadExpenses();
    if (localExpenses.length === 0) {
      setSyncMessage({ type: 'info', text: 'No local expenses found to migrate.' });
      setTimeout(() => setSyncMessage(null), 4000);
      return;
    }
    if (!window.confirm(`Migrate ${localExpenses.length} local expense(s) to Firestore? Duplicates are skipped automatically.`)) return;
    setMigrating(true);
    try {
      const token = localStorage.getItem('netpacific_token');
      if (!token) {
        setSyncMessage({ type: 'error', text: 'Not authenticated. Please log in first.' });
        setTimeout(() => setSyncMessage(null), 4000);
        return;
      }
      const res = await fetch(`${API_BASE}/api/project-expenses/migrate-from-localstorage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ expenses: localExpenses }),
      });
      const data = await res.json().catch(() => ({ success: false }));
      if (data.success) {
        setSyncMessage({ type: 'success', text: `Migration complete: ${data.inserted} expense(s) saved to cloud, ${data.skipped} already existed.` });
      } else {
        setSyncMessage({ type: 'error', text: data.error || 'Migration failed. You may not have admin access.' });
      }
    } catch {
      setSyncMessage({ type: 'error', text: 'Migration failed. Check your connection.' });
    } finally {
      setMigrating(false);
      setTimeout(() => setSyncMessage(null), 6000);
    }
  };

  const handleDeleteExpense = async (expense: ProjectExpense) => {
    if (!window.confirm('Delete this expense? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('netpacific_token');
      const endpoint = expense.scope === 'overhead' ? 'overhead-expenses' : 'project-expenses';
      const res = await fetch(`${API_BASE}/api/${endpoint}/${expense.id}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({ success: false }));
      if (data.success) {
        // Best-effort OneDrive cleanup of the orphaned receipt file — Firestore is
        // the source of truth, so a failure here never blocks the delete.
        if (expense.receiptRef?.oneDriveId) {
          void deleteDriveItem('server', 'server', expense.receiptRef.oneDriveId);
        }
        await fetchExpenses();
      }
    } catch {
      // silent — expense stays in list if request fails
    }
  };

  // Sync-sourced rows are managed by their origin job (PO/liquidation/payroll sync) —
  // editing or moving them here would be overwritten or double-counted on re-sync.
  // Recurring postings are written once and never re-synced, so they stay editable.
  const isUserManagedRow = (e: ProjectExpense) =>
    !e.sourceType || e.sourceType === 'manual' || e.sourceType === 'receipt_scan' || e.sourceType === 'migrated' || e.sourceType === 'recurring';

  const openEditDialog = (expense: ProjectExpense) => {
    setEditError('');
    setEditFields({
      description: expense.description || '',
      remarks: expense.remarks || '',
      amount: String(expense.amount ?? ''),
      date: expense.date || '',
      category: expense.category || '',
      supplier: expense.supplier || '',
      invoiceNo: expense.invoiceNo || '',
      invoiceType: expense.invoiceType || '',
      vat: expense.vat != null ? String(expense.vat) : '',
      tin: expense.tin || '',
    });
    setEditExpense(expense);
  };

  const handleSaveEdit = async () => {
    if (!editExpense) return;
    const amount = Number(editFields.amount) || 0;
    if (amount <= 0) { setEditError('Enter an amount greater than 0'); return; }
    setSavingEdit(true);
    setEditError('');
    try {
      const token = localStorage.getItem('netpacific_token');
      const endpoint = editExpense.scope === 'overhead' ? 'overhead-expenses' : 'project-expenses';
      const res = await fetch(`${API_BASE}/api/${endpoint}/${editExpense.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          description: editFields.description.trim() || '—',
          remarks: editFields.remarks.trim(),
          amount,
          date: editFields.date,
          category: editFields.category.trim() || 'Others',
          supplier: editFields.supplier.trim(),
          invoiceNo: editFields.invoiceNo.trim(),
          invoiceType: editFields.invoiceType,
          tin: editFields.tin.trim(),
          ...(editFields.vat !== '' && !isNaN(Number(editFields.vat)) ? { vat: Number(editFields.vat) } : {}),
        }),
      });
      const data = await res.json().catch(() => ({ success: false }));
      if (data.success) {
        setEditExpense(null);
        await fetchExpenses();
      } else {
        setEditError(data.error || 'Failed to save changes');
      }
    } catch {
      setEditError('Failed to save changes. Check your connection.');
    } finally {
      setSavingEdit(false);
    }
  };

  // Inline-edit the deductible flag directly from the table (accounting correction),
  // mirroring the Tax Filer Ledger's same inline control.
  const setExpenseDeductibleFlag = async (expense: ProjectExpense, value: boolean | null) => {
    const endpoint = expense.scope === 'overhead' ? 'overhead-expenses' : 'project-expenses';
    const prevExpenses = expenses;
    setSavingDeductibleId(expense.id);
    setExpenses((prev) => prev.map((e) => (e.id === expense.id && e.scope === expense.scope ? { ...e, deductible: value } : e)));
    try {
      const token = localStorage.getItem('netpacific_token');
      const res = await fetch(`${API_BASE}/api/${endpoint}/${expense.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ deductible: value }),
      });
      const data = await res.json().catch(() => ({ success: false }));
      if (!data.success) throw new Error(data.error || 'Update failed');
    } catch {
      setExpenses(prevExpenses); // revert
      setScanSnackbar({ open: true, severity: 'error', message: 'Could not update the deductible flag.' });
    } finally {
      setSavingDeductibleId(null);
    }
  };

  const openMoveDialog = (expense: ProjectExpense) => {
    setMoveError('');
    setMoveProjectId('');
    // Preselect the category if it exists on the other side, otherwise force a re-pick.
    const targetList: readonly string[] = expense.scope === 'overhead' ? PROJECT_EXPENSE_CATEGORIES : OVERHEAD_CATEGORIES;
    setMoveCategory(targetList.includes(expense.category) ? expense.category : '');
    setMoveExpense(expense);
  };

  const handleMove = async () => {
    if (!moveExpense) return;
    if (moveExpense.scope === 'overhead' && !moveProjectId) { setMoveError('Select a project'); return; }
    setMoving(true);
    setMoveError('');
    try {
      const token = localStorage.getItem('netpacific_token');
      const project = allProjects.find((p) => String(p.id) === moveProjectId);
      const url = moveExpense.scope === 'overhead'
        ? `${API_BASE}/api/overhead-expenses/${moveExpense.id}/convert-to-project`
        : `${API_BASE}/api/project-expenses/${moveExpense.id}/convert-to-overhead`;
      const body = moveExpense.scope === 'overhead'
        ? { projectId: moveProjectId, projectName: project?.project_name ?? '—', ...(moveCategory ? { category: moveCategory } : {}) }
        : { ...(moveCategory ? { category: moveCategory } : {}) };
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({ success: false }));
      if (data.success) {
        setMoveExpense(null);
        setScanSnackbar({ open: true, severity: 'success', message: moveExpense.scope === 'overhead' ? 'Moved to project expenses.' : 'Moved to overhead expenses.' });
        await fetchExpenses();
      } else {
        setMoveError(data.error || 'Failed to move expense');
      }
    } catch {
      setMoveError('Failed to move expense. Check your connection.');
    } finally {
      setMoving(false);
    }
  };

  // Attach-receipt-later: user picks a photo for an existing row; upload it to OneDrive
  // (project folder for project rows, overhead folder otherwise) and PATCH the row's
  // receiptRef on the matching collection's endpoint. No crop/AI parse — the expense's
  // fields already exist; this only backfills the missing image.
  const openAttachReceipt = (expense: ProjectExpense) => {
    attachReceiptTargetRef.current = expense;
    attachReceiptInputRef.current?.click();
  };

  const handleAttachReceiptInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const target = attachReceiptTargetRef.current;
    attachReceiptTargetRef.current = null;
    if (!file || !target) return;
    setAttachingReceiptId(target.id);
    try {
      const token = localStorage.getItem('netpacific_token');
      const safeFile = await convertHeicToJpeg(file);
      const compressed = await compressForUpload(safeFile);
      const contentBase64 = await blobToBase64(compressed);
      const year = String((target.date || '').slice(0, 4) || new Date().getFullYear());
      // Preserve the real extension (jpg after HEIC conversion, pdf/png/etc. as-is)
      // instead of forcing .jpg — a PDF saved with a fake .jpg name won't open.
      const extMatch = safeFile.name.match(/\.[a-z0-9]+$/i);
      const ext = extMatch ? extMatch[0] : (safeFile.type === 'application/pdf' ? '.pdf' : '.jpg');
      const filename = `SCAN-${Date.now()}${ext}`;
      const project = target.scope === 'project' ? allProjects.find((p) => String(p.id) === String(target.projectId)) : undefined;
      const folderPath = target.scope === 'project'
        ? `Project Receipts/${project?.project_no || target.projectId}/${year}`
        : `00 Overhead Receipts/${year}`;
      const upRes = await fetch(`${API_BASE}/api/onedrive/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ folderPath, filename, contentBase64 }),
      });
      const upData = await upRes.json().catch(() => ({ ok: false })) as { ok: boolean; id?: string; webUrl?: string };
      if (!upData.ok || !upData.id || !upData.webUrl) {
        setScanSnackbar({ open: true, severity: 'error', message: 'Receipt upload to OneDrive failed. Try again.' });
        return;
      }
      const endpoint = target.scope === 'overhead' ? 'overhead-expenses' : 'project-expenses';
      const patchRes = await fetch(`${API_BASE}/api/${endpoint}/${target.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ receiptRef: { oneDriveId: upData.id, webUrl: upData.webUrl, filename } }),
      });
      const patchData = await patchRes.json().catch(() => ({ success: false }));
      if (patchData.success) {
        setScanSnackbar({ open: true, severity: 'success', message: 'Receipt attached.' });
        await fetchExpenses();
      } else {
        setScanSnackbar({ open: true, severity: 'error', message: patchData.error || 'Failed to attach receipt to the expense.' });
      }
    } catch (err) {
      setScanSnackbar({ open: true, severity: 'error', message: err instanceof Error ? err.message : 'Failed to attach receipt' });
    } finally {
      setAttachingReceiptId(null);
    }
  };

  const openPromoteDialog = async (expense: ProjectExpense) => {
    setPromoteExpense(expense);
    setPromoteUserId('');
    setPromoteCaId('');
    setPromoteCAs([]);
    setPromoteDrafts([]);
    setPromoteTargetLiquidationId('');
    setPromoteError('');
    try {
      const token = localStorage.getItem('netpacific_token');
      const res = await fetch(`${API_BASE}/api/users`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const data = await res.json().catch(() => ({ success: false }));
      if (data.success) setPromoteUsers(data.users);
    } catch {
      // silent — dialog still opens, employee list just stays empty
    }
  };

  const handlePromoteUserChange = async (userId: string) => {
    setPromoteUserId(userId);
    setPromoteCaId('');
    setPromoteCAs([]);
    setPromoteDrafts([]);
    setPromoteTargetLiquidationId('');
    if (!userId) return;
    try {
      const token = localStorage.getItem('netpacific_token');
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      const [caRes, liqRes] = await Promise.all([
        fetch(`${API_BASE}/api/cash-advances`, { headers }),
        fetch(`${API_BASE}/api/liquidations`, { headers }),
      ]);
      const caData = await caRes.json().catch(() => ({ success: false }));
      if (caData.success) {
        const eligible = (caData.cash_advances || []).filter((ca: { user_id: string; status: string; balance_remaining: number }) =>
          String(ca.user_id) === userId && ca.status === 'approved' && (Number(ca.balance_remaining) || 0) > 0
        );
        setPromoteCAs(eligible.map((ca: { id: string; ca_no: string; balance_remaining: number }) => ({ id: ca.id, ca_no: ca.ca_no, balance_remaining: Number(ca.balance_remaining) || 0 })));
      }
      const liqData = await liqRes.json().catch(() => ({ success: false }));
      if (liqData.success) {
        // Both drafts and submitted liquidations of this employee are valid append
        // targets — appending to a submitted one goes through the same revision
        // machinery as "Edit (applies immediately)" so CA/reimbursement stay correct.
        const eligible = (liqData.liquidations || []).filter((l: { user_id: string; status: string }) =>
          String(l.user_id) === userId && (l.status === 'draft' || l.status === 'submitted')
        );
        setPromoteDrafts(eligible.map((l: { id: string; form_no: string; total_amount: number; status: string }) => ({ id: l.id, form_no: l.form_no, total_amount: Number(l.total_amount) || 0, status: l.status })));
      }
    } catch {
      // silent — falls back to a new standalone out-of-pocket liquidation
    }
  };

  const handlePromote = async () => {
    if (!promoteExpense || !promoteUserId) return;
    setPromoting(true);
    setPromoteError('');
    try {
      const token = localStorage.getItem('netpacific_token');
      const endpoint = promoteExpense.scope === 'overhead' ? 'overhead-expenses' : 'project-expenses';
      const res = await fetch(`${API_BASE}/api/${endpoint}/${promoteExpense.id}/promote-to-liquidation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          userId: promoteUserId,
          ...(promoteTargetLiquidationId
            ? { targetLiquidationId: promoteTargetLiquidationId }
            : (promoteCaId ? { caId: promoteCaId } : {})),
        }),
      });
      const data = await res.json().catch(() => ({ success: false }));
      if (data.success) {
        setPromoteExpense(null);
        await fetchExpenses();
      } else {
        setPromoteError(data.error || 'Failed to promote expense');
      }
    } catch {
      setPromoteError('Failed to promote expense. Check your connection.');
    } finally {
      setPromoting(false);
    }
  };

  const setEdit = useCallback((blob: Blob | null, quad: Quad | null) => {
    setEditUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return blob ? URL.createObjectURL(blob) : null; });
    setEditBlob(blob);
    setEditQuad(quad);
  }, []);

  useEffect(() => () => { if (editUrl) URL.revokeObjectURL(editUrl); }, [editUrl]);

  useEffect(() => { if (!addExpenseOpen) pendingReceiptRef.current = null; }, [addExpenseOpen]);

  // `?scope=overhead` presets the filter — used by the old /finance/overhead-expenses
  // route, which now redirects here. Run once on mount.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('scope') === 'overhead') {
      setSelectedProjectId(OVERHEAD_SENTINEL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Best-effort lazy receipt thumbnails via the server-side OneDrive proxy
  // (ported from OverheadExpensesPage). Never throws.
  useEffect(() => {
    let cancelled = false;
    const loadThumbs = async () => {
      const missing = expenses.filter((e) => e.receiptRef?.oneDriveId && !thumbs[e.receiptRef.oneDriveId]);
      if (missing.length === 0) return;
      try {
        const entries: Array<[string, string]> = [];
        for (const e of missing) {
          const oneDriveId = e.receiptRef!.oneDriveId;
          const url = await getDriveItemThumbnailUrl('server', 'server', oneDriveId);
          if (url) entries.push([oneDriveId, url]);
        }
        if (!cancelled && entries.length > 0) {
          setThumbs((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
        }
      } catch (err) {
        console.warn('[OneDrive] receipt thumbnail fetch failed:', err);
      }
    };
    void loadThumbs();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses]);

  // Revoke the viewer pane's object URL when it changes or the pane closes.
  useEffect(() => () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); }, [viewer]);

  const openReceiptViewer = async (expense: ProjectExpense) => {
    if (!expense.receiptRef?.oneDriveId) return;
    setViewer({ expense, url: null });
    try {
      const blob = await fetchDriveItemBlob('server', 'server', expense.receiptRef.oneDriveId);
      setViewer((prev) => (prev && prev.expense.id === expense.id ? { ...prev, url: URL.createObjectURL(blob) } : prev));
    } catch (err) {
      console.warn('[OneDrive] receipt image fetch failed:', err);
      setScanSnackbar({ open: true, severity: 'error', message: 'Could not load the receipt image. Use "Open in OneDrive" instead.' });
    }
  };

  // Bakes the viewer's current rotation into the file and overwrites the same
  // OneDrive item, so it stays upright on a plain download too. id/webUrl are
  // unchanged, so nothing in Firestore needs to be touched.
  const saveReceiptRotation = async (oneDriveId: string, rotatedBlob: Blob) => {
    await replaceDriveItemContent(oneDriveId, rotatedBlob);
    // The cached thumbnail is now stale — refetch it in place.
    try {
      const url = await getDriveItemThumbnailUrl('server', 'server', oneDriveId);
      if (url) setThumbs((prev) => ({ ...prev, [oneDriveId]: url }));
    } catch {
      // best-effort — the full viewer already shows the corrected image
    }
  };

  const applyParsedReceipt = async (parsed: ParsedReceipt, receiptBase64: string, gen: number) => {
    if (gen !== scanGenRef.current) return;
    const amt = parsed.total ?? parsed.subtotal;
    if (typeof amt === 'number' && amt > 0) setExpenseAmount(String(amt));
    if (parsed.date) setExpenseDate(parsed.date);
    const scanCategoryList: readonly string[] = expenseScope === 'overhead' ? OVERHEAD_CATEGORIES : PROJECT_EXPENSE_CATEGORIES;
    if (parsed.suggestedCategory && scanCategoryList.includes(parsed.suggestedCategory)) {
      setExpenseCategory(parsed.suggestedCategory);
    }
    const desc = parsed.vendor || parsed.lineItems?.[0]?.description;
    if (desc && !expenseDescription.trim()) setExpenseDescription(desc);
    if (parsed.vendor) setExpenseSupplier(parsed.vendor);
    if (parsed.invoiceNumber) setExpenseInvoiceNo(parsed.invoiceNumber);
    if (parsed.invoiceType) {
      const matched = INVOICE_TYPES.find((t) => t.toLowerCase() === parsed.invoiceType?.toLowerCase());
      setExpenseInvoiceType(matched || '');
    }
    if (parsed.tax !== null && parsed.tax !== undefined) setExpenseVat(String(parsed.tax));
    if (typeof parsed.deductible === 'boolean') setExpenseDeductible(parsed.deductible);
    if (parsed.deductibleReason) setExpenseDeductibleReason(parsed.deductibleReason);
    const lowConf = typeof parsed.confidence === 'number' && parsed.confidence < 0.5;
    const pct = typeof parsed.confidence === 'number' ? Math.round(parsed.confidence * 100) : null;
    if (lowConf) {
      setScanSnackbar({ open: true, severity: 'warning', message: `Low confidence${pct !== null ? ` (${pct}%)` : ''} — please verify amount, date & category. Parsed: ${parsed.vendor || 'Unknown vendor'} (PHP ${Number(amt ?? 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })})` });
    } else {
      setScanSnackbar({ open: true, severity: 'success', message: `Parsed: ${parsed.vendor || 'Unknown vendor'} (PHP ${Number(amt ?? 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })})` });
    }
    // Duplicate-receipt check — best-effort, never blocks the scan flow.
    const imageHash = await computeImageHash(receiptBase64);
    if (gen !== scanGenRef.current) return;
    setExpenseImageHash(imageHash || null);
    try {
      const matchesMap = await checkDuplicates([{
        key: 'scan',
        supplier: parsed.vendor || undefined,
        invoiceNo: parsed.invoiceNumber || undefined,
        amount: typeof amt === 'number' ? amt : undefined,
        date: parsed.date || undefined,
        imageHash: imageHash || undefined,
      }]);
      if (gen !== scanGenRef.current) return;
      const matches: DuplicateMatch[] = matchesMap.get('scan') || [];
      if (matches.length > 0) {
        const warnings = matches.map(describeMatch);
        setExpenseDuplicateWarnings(warnings);
        setScanSnackbar({ open: true, severity: 'warning', message: `Possible duplicate receipt: ${warnings[0]}` });
      }
    } catch (err) {
      console.warn('[ExpenseMonitoring] duplicate check failed:', err);
    }
  };

  // Phase 1: PDFs parse directly; photos auto-detect their corners and open the crop dialog.
  const handleScanInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    scanGenRef.current += 1;
    const selectionGen = scanGenRef.current;
    const validationError = validateReceiptFile(file);
    if (validationError) {
      setScanSnackbar({ open: true, severity: 'error', message: validationError });
      setIsScanning(false);
      return;
    }
    setIsScanning(true);
    try {
      if (isPdfReceipt(file)) {
        setExpenseImageHash(null);
        setExpenseDuplicateWarnings([]);
        pendingReceiptRef.current = file;
        const receiptBase64 = await blobToBase64(file);
        const parsed = await parseReceipt(receiptBase64, getReceiptParseMimeType(file));
        await applyParsedReceipt(parsed, receiptBase64, selectionGen);
        return;
      }
      const safeFile = await convertHeicToJpeg(file);
      const quad = await suggestReceiptCrop(safeFile);
      if (selectionGen !== scanGenRef.current) return;
      setEdit(safeFile, quad);
    } catch (err) {
      if (selectionGen === scanGenRef.current) {
        setScanSnackbar({ open: true, severity: 'error', message: err instanceof Error ? err.message : 'Could not process receipt' });
      }
    } finally {
      if (selectionGen === scanGenRef.current) setIsScanning(false);
    }
  };

  const retakeScan = () => {
    setEdit(null, null);
    pendingReceiptRef.current = null;
    setExpenseImageHash(null);
    setExpenseDuplicateWarnings([]);
    scanInputRef.current?.click();
  };

  // Phase 2: user confirms the crop — flatten the quad, then parse with Gemini.
  const confirmScanCrop = async (quad: Quad) => {
    if (!editBlob) return;
    // Clear any hash/warnings left over from a previous scan up front, and bump the
    // generation counter so a stale in-flight check from a cancelled/retaken scan
    // can't repopulate state after this one has started (or been cleared again).
    setExpenseImageHash(null);
    setExpenseDuplicateWarnings([]);
    scanGenRef.current += 1;
    const gen = scanGenRef.current;
    setIsScanning(true);
    try {
      const flattened = await perspectiveCropToBlob(editBlob, quad);
      const croppedFile = new File([flattened], `receipt-${Date.now()}.jpg`, { type: 'image/jpeg' });
      pendingReceiptRef.current = croppedFile;
      const imageBase64 = await blobToBase64(flattened);
      const parsed = await parseReceipt(imageBase64, 'image/jpeg');
      await applyParsedReceipt(parsed, imageBase64, gen);
      setEdit(null, null);
    } catch (err) {
      setScanSnackbar({ open: true, severity: 'error', message: err instanceof Error ? err.message : 'Failed to parse receipt' });
    } finally {
      setIsScanning(false);
    }
  };

  const handleExpenseInvestorChange = async (investor: string) => {
    setExpenseFundingInvestor(investor);
    setExpenseLinkedInvestmentId('');
    setExpenseLinkedInvestments([]);
    if (!investor) return;
    try {
      const token = localStorage.getItem('netpacific_token');
      const res = await fetch(`${API_BASE}/api/investments`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const data = await res.json().catch(() => ({ success: false }));
      if (data.success) {
        const eligible = (data.investments || []).filter((inv: { investor: string; sourceType?: string }) =>
          inv.investor === investor && inv.sourceType !== 'expense_sync'
        );
        setExpenseLinkedInvestments(eligible.map((inv: { id: string; date: string; category: string; description: string; amount: number }) => ({
          id: inv.id, date: inv.date, category: inv.category, description: inv.description, amount: Number(inv.amount) || 0,
        })));
      }
    } catch {
      // silent — admin-only endpoint; non-admins just won't see linkable investments
    }
  };

  const handleAddExpense = async () => {
    const pid = expenseProjectId === '' ? null : String(expenseProjectId);
    const amount = Number(expenseAmount) || 0;
    if (expenseScope === 'project' && pid == null) return;
    if (amount <= 0) return;
    if (expenseFundingType === 'investor_outofpocket' && !expenseFundingInvestor) return;
    const project = expenseScope === 'project' ? allProjects.find((p) => String(p.id) === pid) : undefined;
    setSavingExpense(true);
    try {
      const token = localStorage.getItem('netpacific_token');
      let receiptRef: { oneDriveId: string; webUrl: string; filename: string } | undefined;
      const pendingFile = pendingReceiptRef.current;
      if (pendingFile) {
        try {
          const compressed = isPdfReceipt(pendingFile) ? pendingFile : await compressForUpload(pendingFile);
          const contentBase64 = await blobToBase64(compressed);
          const year = String(new Date().getFullYear());
          const filename = makeReceiptUploadFilename(pendingFile);
          const folderPath = expenseScope === 'project'
            ? `Project Receipts/${project?.project_no || pid}/${year}`
            : `00 Overhead Receipts/${year}`;
          const upRes = await fetch(`${API_BASE}/api/onedrive/upload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ folderPath, filename, contentBase64 }),
          });
          const upData = await upRes.json().catch(() => ({ ok: false })) as { ok: boolean; id?: string; webUrl?: string };
          if (upData.ok && upData.id && upData.webUrl) {
            receiptRef = { oneDriveId: upData.id, webUrl: upData.webUrl, filename };
          } else {
            console.warn('[OneDrive] expense receipt upload failed or returned not ok:', upData);
          }
        } catch (err) {
          console.warn('[OneDrive] expense receipt upload failed (expense still saved):', err);
        }
      }
      const endpoint = expenseScope === 'project' ? 'project-expenses' : 'overhead-expenses';
      const res = await fetch(`${API_BASE}/api/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          ...(expenseScope === 'project' ? { projectId: pid, projectName: project?.project_name ?? '—' } : {}),
          description: expenseDescription.trim() || '—',
          ...(expenseRemarks.trim() ? { remarks: expenseRemarks.trim() } : {}),
          amount,
          date: expenseDate,
          category: expenseCategory.trim() || '—',
          sourceType: pendingFile ? 'receipt_scan' : 'manual',
          ...(expenseSupplier.trim() ? { supplier: expenseSupplier.trim() } : {}),
          ...(expenseInvoiceNo.trim() ? { invoiceNo: expenseInvoiceNo.trim() } : {}),
          ...(expenseInvoiceType ? { invoiceType: expenseInvoiceType } : {}),
          ...(expenseVat && !isNaN(Number(expenseVat)) ? { vat: Number(expenseVat) } : {}),
          ...(expenseTin.trim() ? { tin: expenseTin.trim() } : {}),
          fundingSource: expenseFundingType === 'investor_outofpocket'
            ? { type: 'investor_outofpocket', treatment: 'equity_contribution', investor: expenseFundingInvestor, ...(expenseLinkedInvestmentId ? { linkedInvestmentId: expenseLinkedInvestmentId } : {}) }
            : { type: 'corporate_bank', treatment: 'company_funded', paidVia: expenseFundingType === 'corporate_card' ? 'corporate_card' : 'bank' },
          ...(typeof expenseDeductible === 'boolean' ? { deductible: expenseDeductible } : {}),
          ...(expenseDeductibleReason ? { deductibleReason: expenseDeductibleReason } : {}),
          ...(receiptRef ? { receiptRef } : {}),
          ...(expenseImageHash ? { imageHash: expenseImageHash } : {}),
        }),
      });
      const data = await res.json().catch(() => ({ success: false }));
      if (data.success) {
        setAddExpenseOpen(false);
        setExpenseProjectId('');
        setExpenseAmount('');
        setExpenseDate(new Date().toISOString().slice(0, 10));
        setExpenseDescription('');
        setExpenseRemarks('');
        setExpenseCategory('');
        setExpenseSupplier('');
        setExpenseInvoiceNo('');
        setExpenseInvoiceType('');
        setExpenseVat('');
        setExpenseTin('');
        setExpenseFundingType('corporate_bank');
        setExpenseFundingInvestor('');
        setExpenseLinkedInvestments([]);
        setExpenseLinkedInvestmentId('');
        setExpenseDeductible(null);
        setExpenseDeductibleReason(null);
        setExpenseImageHash(null);
        setExpenseDuplicateWarnings([]);
        pendingReceiptRef.current = null;
        await fetchExpenses();
      }
    } catch {
      // silent
    } finally {
      setSavingExpense(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setSelectedYear(Number(event.target.value));
  };

  if (isChildRoute) {
    return <Outlet />;
  }

  if (loading) {
    return (
      <Box sx={{ height: '100%' }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading expense data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 1, justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Expense Monitoring
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <ScanWithPhoneButton />
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={handleSyncFromPO}
            sx={{ borderColor: NET_PACIFIC_COLORS.primary, color: NET_PACIFIC_COLORS.primary }}
          >
            Sync from PO
          </Button>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={handleSyncFromLiquidation}
            sx={{ borderColor: NET_PACIFIC_COLORS.accent1, color: NET_PACIFIC_COLORS.accent1 }}
          >
            Sync from Liquidation
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setExpenseSupplier(''); setExpenseInvoiceNo(''); setExpenseInvoiceType(''); setExpenseVat(''); setExpenseTin(''); pendingReceiptRef.current = null;
              setExpenseImageHash(null); setExpenseDuplicateWarnings([]);
              // Default the dialog's scope/project to whatever the table is filtered to.
              setExpenseScope(selectedProjectId === OVERHEAD_SENTINEL ? 'overhead' : 'project');
              if (selectedProjectId !== '' && selectedProjectId !== OVERHEAD_SENTINEL && selectedProjectId !== ALL_PROJECTS_SENTINEL) setExpenseProjectId(selectedProjectId);
              setAddExpenseOpen(true);
            }}
            sx={{ backgroundColor: NET_PACIFIC_COLORS.primary, '&:hover': { backgroundColor: NET_PACIFIC_COLORS.secondary } }}
          >
            Add Expense
          </Button>
        </Box>
      </Box>

      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
        PO totals are read from browser storage.
      </Typography>

      {(financeFocus.focusedKey || financeFocus.focusError) && (
        <Alert
          severity={financeFocus.focusError ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          action={(
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
              {financeFocus.hasBackSource && <Button color="inherit" size="small" onClick={financeFocus.backToSource}>Back to source</Button>}
              <Button color="inherit" size="small" onClick={financeFocus.clearFocus}>Clear focus</Button>
            </Stack>
          )}
        >
          {financeFocus.focusError || 'Filters and pagination were adjusted to show the exact expense.'}
        </Alert>
      )}

      {syncMessage && (
        <Box
          sx={{
            py: 1,
            px: 2,
            mb: 2,
            borderRadius: 1,
            bgcolor: syncMessage.type === 'success' ? 'success.light' : syncMessage.type === 'error' ? 'error.light' : 'info.light',
            color: syncMessage.type === 'success' ? 'success.dark' : syncMessage.type === 'error' ? 'error.dark' : 'info.dark',
          }}
        >
          {syncMessage.text}
        </Box>
      )}

      {localStorageCount > 0 && (
        <Box
          sx={{
            py: 1, px: 2, mb: 2, borderRadius: 1,
            bgcolor: 'info.light', color: 'info.dark',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
          }}
        >
          <Typography variant="body2">
            {localStorageCount} expense{localStorageCount !== 1 ? 's' : ''} stored locally (browser only). Migrate to cloud so all team members can see them.
          </Typography>
          <Button
            size="small" variant="outlined" disabled={migrating}
            onClick={handleMigrateToCloud}
            sx={{ borderColor: 'info.dark', color: 'info.dark', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {migrating ? 'Migrating…' : 'Migrate to cloud'}
          </Button>
        </Box>
      )}

      {expensesLoading && <LinearProgress sx={{ mb: 1 }} />}

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select value={selectedYear} onChange={handleYearChange} label="Year">
            <MenuItem value={0}>All years</MenuItem>
            {YEAR_OPTIONS.map((year) => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Month</InputLabel>
          <Select
            value={selectedMonth}
            onChange={(e) => { setSelectedMonth(Number(e.target.value)); setSelectedQuarter(0); }}
            label="Month"
          >
            <MenuItem value={0}>All months</MenuItem>
            {Array.from({ length: 12 }, (_, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Quarter</InputLabel>
          <Select
            value={selectedQuarter}
            onChange={(e) => { setSelectedQuarter(Number(e.target.value)); setSelectedMonth(0); }}
            label="Quarter"
          >
            <MenuItem value={0}>All quarters</MenuItem>
            {[1, 2, 3, 4].map((q) => (
              <MenuItem key={q} value={q}>Q{q}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Scope</InputLabel>
          <Select
            value={selectedProjectId === '' ? '' : selectedProjectId}
            onChange={(e) => { const v = String(e.target.value); setSelectedProjectId(v === '' ? '' : v); }}
            label="Scope"
          >
            <MenuItem value="">All (projects + overhead)</MenuItem>
            <MenuItem value={ALL_PROJECTS_SENTINEL}>All projects (no overhead)</MenuItem>
            <MenuItem value={OVERHEAD_SENTINEL}>Overhead (no project)</MenuItem>
            {allProjects.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>{p.project_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedProjectId !== '' && selectedProjectId !== OVERHEAD_SENTINEL && selectedProjectId !== ALL_PROJECTS_SENTINEL && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/finance/projects/${selectedProjectId}/expenses`)}
          >
            Full Report
          </Button>
        )}
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${NET_PACIFIC_COLORS.primary} 0%, ${NET_PACIFIC_COLORS.accent1} 100%)`, color: 'white' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9 }}>Total Budget</Typography>
              <Typography variant="h5" component="div" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {formatCurrency(expenseMetrics.totalBudget)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${NET_PACIFIC_COLORS.info} 0%, #a29bfe 100%)`, color: 'white' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9 }}>Total Spent</Typography>
              <Typography variant="h5" component="div" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {formatCurrency(expenseMetrics.totalSpent)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.75rem', mt: 0.5 }}>
                {expenseMetrics.spentPercentage.toFixed(1)}% of budget
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card sx={{
            height: '100%',
            color: 'white',
            background: expenseMetrics.statusColor === 'red'
              ? 'linear-gradient(135deg, #c62828 0%, #e57373 100%)'
              : expenseMetrics.statusColor === 'yellow'
                ? 'linear-gradient(135deg, #f9a825 0%, #ffd54f 100%)'
                : 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)',
          }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9 }}>Remaining Budget</Typography>
              <Typography variant="h5" component="div" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {formatCurrency(expenseMetrics.totalRemaining)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card sx={{
            height: '100%',
            color: 'white',
            background: expenseMetrics.overBudgetCategories > 0
              ? 'linear-gradient(135deg, #c62828 0%, #e57373 100%)'
              : 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)',
          }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.9 }}>
                {selectedProjectId === '' || selectedProjectId === ALL_PROJECTS_SENTINEL ? 'Over Budget Projects' : 'Over Budget'}
              </Typography>
              <Typography variant="h5" component="div" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {expenseMetrics.overBudgetCategories}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {(user?.role === 'admin' || user?.role === 'superadmin') && (
        <RecurringExpensesTray onPosted={() => { fetchExpenses(); }} />
      )}

      {/* Charts Row */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {/* Expense Distribution Pie Chart */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ 
            p: 2, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0'
          }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: NET_PACIFIC_COLORS.primary }}>
              Expense Distribution by Project
            </Typography>
            {/* Phones: taller chart + auto-height legend, so many project names wrap inside the card instead of spilling out */}
            <ResponsiveContainer width="100%" height={isPhone ? 440 : 300}>
              <PieChart>
                <Pie
                  data={pieChartData.map((item, index) => ({
                    ...item,
                    fill: [
                      NET_PACIFIC_COLORS.primary,
                      NET_PACIFIC_COLORS.success,
                      NET_PACIFIC_COLORS.warning,
                      NET_PACIFIC_COLORS.info,
                      NET_PACIFIC_COLORS.error,
                      NET_PACIFIC_COLORS.accent1,
                      NET_PACIFIC_COLORS.accent2
                    ][index % 7]
                  }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry: any) => {
                    const percent = entry.percent || 0;
                    const name = entry.name || '';
                    return percent > 5 ? `${name}\n${(percent * 100).toFixed(0)}%` : '';
                  }}
                  outerRadius={80}
                  dataKey="value"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: number) => [formatCurrency(value), '']}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    fontSize: '12px'
                  }}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={isPhone ? undefined : 24}
                  iconType="circle"
                  wrapperStyle={{
                    paddingTop: '10px',
                    fontSize: '11px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Budget vs Actual Bar Chart */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ 
            p: 2, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0'
          }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: NET_PACIFIC_COLORS.primary }}>
              Budget vs Actual Spending
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={budgetVsActualData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <defs>
                  <linearGradient id="budgetGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.85}/>
                    <stop offset="95%" stopColor="#93c5fd" stopOpacity={0.35}/>
                  </linearGradient>
                  <linearGradient id="spentGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#fdba74" stopOpacity={0.4}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis 
                  dataKey="category" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  tickFormatter={(value) => `₱${(value / 1000000).toFixed(1)}M`} 
                />
                <Tooltip 
                  formatter={(value: number) => [formatCurrency(value), '']}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    fontSize: '12px'
                  }}
                />
                <Legend iconSize={8} />
                <Bar dataKey="budget" fill="url(#budgetGradient)" name="Budget" radius={[3, 3, 0, 0]} />
                <Bar dataKey="spent" fill="url(#spentGradient)" name="Spent" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Monthly Trend */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ 
            p: 2, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0'
          }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: NET_PACIFIC_COLORS.primary }}>
              Monthly Expense Trend
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyExpenseData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <defs>
                  <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={NET_PACIFIC_COLORS.primary} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={NET_PACIFIC_COLORS.primary} stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis 
                  dataKey="month" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  tickFormatter={(value) => `₱${(value / 1000000).toFixed(1)}M`} 
                />
                <Tooltip 
                  formatter={(value: number) => [formatCurrency(value), 'Expense Amount']}
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    fontSize: '12px'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="total" 
                  stroke={NET_PACIFIC_COLORS.primary} 
                  fill="url(#areaGradient)"
                  strokeWidth={3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Expenses Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Recent Expenses</Typography>
          {isPhone ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">Sort</Typography>
                {([['date', 'Date'], ['amount', 'Amount'], ['project', 'Project'], ['category', 'Category']] as const).map(([k, label]) => (
                  <Button key={k} size="small" variant={sortKey === k ? 'contained' : 'outlined'} onClick={() => handleSort(k)} sx={{ minWidth: 0 }}>
                    {label}{sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </Button>
                ))}
              </Box>
              {tableRows.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  {selectedYear === 0
                    ? 'No expenses yet. Use the Add Expense button to add an expense.'
                    : `No expenses in ${selectedYear}. Use the Add Expense button to add an expense.`}
                </Typography>
              )}
              {tableRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((expense) => {
                  const project = expense.scope === 'project' ? allProjects.find((p) => String(p.id) === String(expense.projectId)) : undefined;
                  const projectNo = project?.project_no || String(project?.item_no ?? project?.id ?? '');
                  const poNumber = project?.po_number
                    ? (project.with_acti ? `${project.po_number} (to ACTI)` : project.po_number)
                    : '';
                  const origin = expenseOrigin(expense);
                  const rowToken = financeFocusToken(origin);
                  const linkedOrigins = linkedOriginsForExpense(expense);
                  const focused = financeFocus.isFocused(origin);
                  return (
                    <Paper
                      key={`${expense.scope}-${expense.id}`}
                      variant="outlined"
                      ref={(element: HTMLDivElement | null) => {
                        if (element) financeRowRefs.current.set(rowToken, element as unknown as HTMLTableRowElement);
                        else financeRowRefs.current.delete(rowToken);
                      }}
                      aria-current={focused ? 'true' : undefined}
                      sx={{ p: 1.25, ...(focused ? { bgcolor: 'rgba(44,90,160,0.14)', outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' } : {}) }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{expense.date}</Typography>
                        <Chip
                          size="small"
                          label={
                            expense.sourceType === 'po_sync' ? 'PO' :
                            expense.sourceType === 'liquidation_sync' ? 'Liquidation' :
                            expense.sourceType === 'migrated' ? 'Migrated' :
                            expense.sourceType === 'receipt_scan' ? 'Scan' :
                              expense.sourceType === 'recurring' ? 'Recurring' : 'Manual'
                          }
                          color={
                            expense.sourceType === 'po_sync' ? 'primary' :
                            expense.sourceType === 'liquidation_sync' ? 'warning' : 'default'
                          }
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, overflowWrap: 'anywhere' }}>{expense.description || '—'}</Typography>
                      {expense.sourceType === 'liquidation_sync' && (expense.liquidationFiledBy || expense.liquidationFiledAt) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Filed by {expense.liquidationFiledBy || 'unknown'}{expense.liquidationFiledAt ? ` on ${expense.liquidationFiledAt}` : ''}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            {expense.scope === 'overhead'
                              ? <Chip size="small" label="Overhead" variant="outlined" color="secondary" />
                              : <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{expense.projectName}</Typography>}
                            {expense.category && <Chip size="small" label={expense.category} variant="outlined" sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }} />}
                          </Box>
                          {expense.scope !== 'overhead' && (projectNo || poNumber) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {[projectNo && `Project ${projectNo}`, poNumber && `PO ${poNumber}`].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                          {expense.remarks && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{expense.remarks}</Typography>}
                        </Box>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{formatCurrency(expense.amount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.75 }}>
                        <Select
                          size="small"
                          variant="standard"
                          disableUnderline
                          value={expense.deductible === true ? 'yes' : expense.deductible === false ? 'no' : 'unmarked'}
                          disabled={savingDeductibleId === expense.id}
                          onChange={(e) => {
                            const v = e.target.value;
                            setExpenseDeductibleFlag(expense, v === 'yes' ? true : v === 'no' ? false : null);
                          }}
                          sx={{
                            fontSize: '0.8rem',
                            color: expense.deductible === true ? 'success.main' : expense.deductible === false ? 'error.main' : 'text.secondary',
                          }}
                        >
                          <MenuItem value="unmarked">Unmarked</MenuItem>
                          <MenuItem value="yes">Deductible</MenuItem>
                          <MenuItem value="no">Non-deductible</MenuItem>
                        </Select>
                        {expense.receiptRef?.oneDriveId && thumbs[expense.receiptRef.oneDriveId] ? (
                          <Box
                            component="img"
                            src={thumbs[expense.receiptRef.oneDriveId]}
                            alt="receipt"
                            onClick={() => openReceiptViewer(expense)}
                            onError={() => setThumbs((prev) => { const next = { ...prev }; delete next[expense.receiptRef!.oneDriveId]; return next; })}
                            sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'pointer' }}
                          />
                        ) : expense.receiptRef?.webUrl ? (
                          <Link component="button" type="button" onClick={() => openReceiptViewer(expense)} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                            <OpenInNewIcon fontSize="inherit" /> Receipt
                          </Link>
                        ) : (
                          <Typography variant="caption" color="text.secondary">No receipt</Typography>
                        )}
                      </Box>
                      {(expense.fundingSource?.type === 'investor_outofpocket' && expense.fundingSource.investor?.trim()) || expense.fundingSource?.paidVia === 'corporate_card' || linkedOrigins.some((l) => l.type !== 'investment') ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
                          {expense.fundingSource?.paidVia === 'corporate_card' && (
                            <Chip size="small" variant="outlined" label="Corporate card" />
                          )}
                          {expense.fundingSource?.type === 'investor_outofpocket' && expense.fundingSource.investor?.trim() && (
                            <Chip
                              size="small" variant="outlined" color="info" icon={<InvestorLinkIcon fontSize="small" />}
                              label={expense.fundingSource.investor}
                              onClick={() => {
                                const investmentOrigin = linkedOrigins.find((linked) => linked.type === 'investment');
                                if (investmentOrigin) navigate(financeFocusUrl(investmentOrigin, `${location.pathname}${location.search}`));
                              }}
                              sx={{ cursor: 'pointer' }}
                            />
                          )}
                          {linkedOrigins.filter((linked) => linked.type !== 'investment').map((linked) => (
                            <Chip
                              key={financeFocusToken(linked)}
                              size="small" variant="outlined"
                              color={linked.type === 'liquidation' ? 'warning' : 'primary'}
                              label={linked.type === 'liquidation' ? 'Open liquidation' : 'Open cash advance'}
                              onClick={() => navigate(financeFocusUrl(linked, `${location.pathname}${location.search}`))}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      ) : null}
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25, mt: 0.5, mx: -0.5 }}>
                        {!expense.receiptRef?.webUrl && (
                          <IconButton onClick={() => openAttachReceipt(expense)} title="Attach receipt photo" color="primary" disabled={attachingReceiptId === expense.id}>
                            {attachingReceiptId === expense.id ? <CircularProgress size={16} /> : <AddAPhotoIcon fontSize="small" />}
                          </IconButton>
                        )}
                        {isUserManagedRow(expense) && (
                          <IconButton onClick={() => openEditDialog(expense)} title="Edit expense" color="primary"><EditIcon fontSize="small" /></IconButton>
                        )}
                        {isUserManagedRow(expense) && (
                          <IconButton onClick={() => openMoveDialog(expense)} title={expense.scope === 'overhead' ? 'Move to a project' : 'Move to overhead'} color="primary"><MoveIcon fontSize="small" /></IconButton>
                        )}
                        {user?.role === 'superadmin' && (expense.sourceType === 'manual' || expense.sourceType === 'receipt_scan') && (
                          <IconButton onClick={() => openPromoteDialog(expense)} title="Promote to employee liquidation" color="primary"><PromoteIcon fontSize="small" /></IconButton>
                        )}
                        <MoneyTrailButton origin={origin} compact onResolved={() => { void fetchExpenses(); }} />
                        <Box sx={{ flex: 1 }} />
                        <IconButton onClick={() => handleDeleteExpense(expense)} title="Delete expense" color="error"><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                    </Paper>
                  );
                })}
            </Box>
          ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{sortLabel('date', 'Date')}</TableCell>
                  <TableCell>{sortLabel('project', 'Project')}</TableCell>
                  <TableCell>Project No.</TableCell>
                  <TableCell>PO Number</TableCell>
                  <TableCell>{sortLabel('category', 'Category')}</TableCell>
                  <TableCell>{sortLabel('description', 'Description Part #')}</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell align="right">{sortLabel('amount', 'Amount', 'right')}</TableCell>
                  <TableCell>Deductible</TableCell>
                  <TableCell>Receipt</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell padding="none" align="center" width={170}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      {selectedYear === 0
                        ? 'No expenses yet. Use the Add Expense button to add an expense.'
                        : `No expenses in ${selectedYear}. Use the Add Expense button to add an expense.`}
                    </TableCell>
                  </TableRow>
                ) : (
                  tableRows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((expense) => {
                      const project = expense.scope === 'project' ? allProjects.find((p) => String(p.id) === String(expense.projectId)) : undefined;
                      const projectNo = project?.project_no || String(project?.item_no ?? project?.id ?? '');
                      const poNumber = project?.po_number
                        ? (project.with_acti ? `${project.po_number} (to ACTI)` : project.po_number)
                        : '—';
                      const origin = expenseOrigin(expense);
                      const rowToken = financeFocusToken(origin);
                      const linkedOrigins = linkedOriginsForExpense(expense);
                      const focused = financeFocus.isFocused(origin);
                      return (
                    <TableRow
                      key={`${expense.scope}-${expense.id}`}
                      ref={(element: HTMLTableRowElement | null) => {
                        if (element) financeRowRefs.current.set(rowToken, element);
                        else financeRowRefs.current.delete(rowToken);
                      }}
                      aria-current={focused ? 'true' : undefined}
                      sx={focused ? {
                        bgcolor: 'rgba(44,90,160,0.14)',
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: '-2px',
                      } : undefined}
                    >
                      <TableCell>{expense.date}</TableCell>
                      <TableCell>
                        {expense.scope === 'overhead'
                          ? <Chip size="small" label="Overhead" variant="outlined" color="secondary" />
                          : expense.projectName}
                      </TableCell>
                      <TableCell>{expense.scope === 'overhead' ? '—' : (projectNo || '—')}</TableCell>
                      <TableCell>{expense.scope === 'overhead' ? '—' : poNumber}</TableCell>
                      <TableCell>{expense.category}</TableCell>
                      <TableCell>
                        {expense.description}
                        {expense.sourceType === 'liquidation_sync' && (expense.liquidationFiledBy || expense.liquidationFiledAt) && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Filed by {expense.liquidationFiledBy || 'unknown'}{expense.liquidationFiledAt ? ` on ${expense.liquidationFiledAt}` : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{expense.remarks || '—'}</TableCell>
                      <TableCell align="right">{formatCurrency(expense.amount)}</TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          variant="standard"
                          disableUnderline
                          value={expense.deductible === true ? 'yes' : expense.deductible === false ? 'no' : 'unmarked'}
                          disabled={savingDeductibleId === expense.id}
                          onChange={(e) => {
                            const v = e.target.value;
                            setExpenseDeductibleFlag(expense, v === 'yes' ? true : v === 'no' ? false : null);
                          }}
                          sx={{
                            fontSize: '0.8rem',
                            color: expense.deductible === true ? 'success.main' : expense.deductible === false ? 'error.main' : 'text.secondary',
                          }}
                        >
                          <MenuItem value="unmarked">Unmarked</MenuItem>
                          <MenuItem value="yes">Deductible</MenuItem>
                          <MenuItem value="no">Non-deductible</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {expense.receiptRef?.oneDriveId && thumbs[expense.receiptRef.oneDriveId] ? (
                          <Box
                            component="img"
                            src={thumbs[expense.receiptRef.oneDriveId]}
                            alt="receipt"
                            onClick={() => openReceiptViewer(expense)}
                            onError={() => setThumbs((prev) => { const next = { ...prev }; delete next[expense.receiptRef!.oneDriveId]; return next; })}
                            sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'pointer' }}
                          />
                        ) : expense.receiptRef?.webUrl ? (
                          <Link component="button" type="button" onClick={() => openReceiptViewer(expense)} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                            <OpenInNewIcon fontSize="inherit" /> View
                          </Link>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            size="small"
                            label={
                              expense.sourceType === 'po_sync' ? 'PO' :
                              expense.sourceType === 'liquidation_sync' ? 'Liquidation' :
                              expense.sourceType === 'migrated' ? 'Migrated' :
                              expense.sourceType === 'receipt_scan' ? 'Scan' :
                              expense.sourceType === 'recurring' ? 'Recurring' : 'Manual'
                            }
                            color={
                              expense.sourceType === 'po_sync' ? 'primary' :
                              expense.sourceType === 'liquidation_sync' ? 'warning' : 'default'
                            }
                          />
                          {expense.fundingSource?.paidVia === 'corporate_card' && (
                            <Chip size="small" variant="outlined" label="Corporate card" />
                          )}
                          {expense.fundingSource?.type === 'investor_outofpocket' && expense.fundingSource.investor?.trim() && (
                            <MuiTooltip title={`${expense.fundingSource.investor}'s own money, counted as capital in the Investment Tracker`}>
                              <Chip
                                size="small"
                                variant="outlined"
                                color="info"
                                icon={<InvestorLinkIcon fontSize="small" />}
                                label={expense.fundingSource.investor}
                                onClick={() => {
                                  const investmentOrigin = linkedOrigins.find((linked) => linked.type === 'investment');
                                  if (investmentOrigin) navigate(financeFocusUrl(investmentOrigin, `${location.pathname}${location.search}`));
                                }}
                                sx={{ cursor: 'pointer' }}
                              />
                            </MuiTooltip>
                          )}
                          {linkedOrigins.filter((linked) => linked.type !== 'investment').map((linked) => (
                            <Chip
                              key={financeFocusToken(linked)}
                              size="small"
                              variant="outlined"
                              color={linked.type === 'liquidation' ? 'warning' : 'primary'}
                              label={linked.type === 'liquidation' ? 'Open liquidation' : 'Open cash advance'}
                              onClick={() => navigate(financeFocusUrl(linked, `${location.pathname}${location.search}`))}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell padding="none" align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {/* Fixed-width slot per action (rendered empty when not applicable to this row)
                            so icons line up in columns instead of packing left and shifting per-row. */}
                        <Box sx={{ display: 'inline-flex', width: 34, justifyContent: 'center' }}>
                          {!expense.receiptRef?.webUrl && (
                            <IconButton size="small" onClick={() => openAttachReceipt(expense)} title="Attach receipt photo" color="primary" disabled={attachingReceiptId === expense.id}>
                              {attachingReceiptId === expense.id ? <CircularProgress size={16} /> : <AddAPhotoIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </Box>
                        <Box sx={{ display: 'inline-flex', width: 34, justifyContent: 'center' }}>
                          {isUserManagedRow(expense) && (
                            <IconButton size="small" onClick={() => openEditDialog(expense)} title="Edit expense" color="primary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        <Box sx={{ display: 'inline-flex', width: 34, justifyContent: 'center' }}>
                          {isUserManagedRow(expense) && (
                            <IconButton size="small" onClick={() => openMoveDialog(expense)} title={expense.scope === 'overhead' ? 'Move to a project' : 'Move to overhead'} color="primary">
                              <MoveIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        <Box sx={{ display: 'inline-flex', width: 34, justifyContent: 'center' }}>
                          {user?.role === 'superadmin' && (expense.sourceType === 'manual' || expense.sourceType === 'receipt_scan') && (
                            <IconButton size="small" onClick={() => openPromoteDialog(expense)} title="Promote to employee liquidation" color="primary">
                              <PromoteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        <Box sx={{ display: 'inline-flex', width: 34, justifyContent: 'center' }}>
                          <IconButton size="small" onClick={() => handleDeleteExpense(expense)} title="Delete expense" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <MoneyTrailButton origin={origin} compact onResolved={() => { void fetchExpenses(); }} />
                      </TableCell>
                    </TableRow>
                  ); })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          <TablePagination
            component="div"
            count={tableRows.length}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 250]}
          />
        </CardContent>
      </Card>

      {/* Add Expense Dialog */}
      <Dialog open={addExpenseOpen} onClose={() => setAddExpenseOpen(false)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>Add New Expense</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <ToggleButtonGroup
                  fullWidth
                  exclusive
                  size="small"
                  color="primary"
                  value={expenseScope}
                  onChange={(_, v: ExpenseScope | null) => {
                    if (!v || v === expenseScope) return;
                    setExpenseScope(v);
                    // The two scopes use different category lists (COGS vs OPEX) — drop a
                    // selection that doesn't exist on the other side.
                    const nextList: readonly string[] = v === 'overhead' ? OVERHEAD_CATEGORIES : PROJECT_EXPENSE_CATEGORIES;
                    if (expenseCategory && !nextList.includes(expenseCategory)) setExpenseCategory('');
                  }}
                >
                  <ToggleButton value="project">Project Expense</ToggleButton>
                  <ToggleButton value="overhead">Overhead Expense</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              {expenseScope === 'project' && (
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Project</InputLabel>
                  <Select
                    label="Project"
                    value={expenseProjectId}
                    onChange={(e) => { const v = String(e.target.value); setExpenseProjectId(v === '' ? '' : v); }}
                  >
                    <MenuItem value="">— Select project —</MenuItem>
                    {allProjects.map((project) => (
                      <MenuItem key={project.id} value={String(project.id)}>
                        {project.project_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Amount"
                  type="number"
                  value={expenseAmount}
                  onChange={(e) => setExpenseAmount(e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date"
                  type="date"
                  value={expenseDate}
                  onChange={(e) => setExpenseDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={expenseDescription}
                  onChange={(e) => setExpenseDescription(e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Remarks"
                  placeholder='e.g. "Lunch out with client", "Company meeting"'
                  value={expenseRemarks}
                  onChange={(e) => setExpenseRemarks(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={expenseCategory}
                    onChange={(e) => setExpenseCategory(e.target.value)}
                  >
                    <MenuItem value="">— Select category —</MenuItem>
                    {(expenseScope === 'overhead' ? OVERHEAD_CATEGORIES : PROJECT_EXPENSE_CATEGORIES).map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                  {expenseCategory === 'Taxes & Licenses' && (
                    <FormHelperText>For BIR percentage tax, put 2551Q in the description. Don't record 1601-C or 0619-E remittances as expenses.</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: expenseFundingType === 'investor_outofpocket' ? 6 : 12 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Paid By</InputLabel>
                  <Select
                    label="Paid By"
                    value={expenseFundingType}
                    onChange={(e) => { setExpenseFundingType(e.target.value as 'corporate_bank' | 'corporate_card' | 'investor_outofpocket'); if (e.target.value !== 'investor_outofpocket') setExpenseFundingInvestor(''); }}
                  >
                    <MenuItem value="corporate_bank">IOCT bank account</MenuItem>
                    <MenuItem value="corporate_card">IOCT corporate card</MenuItem>
                    <MenuItem value="investor_outofpocket">Owner's own money (capital)</MenuItem>
                  </Select>
                  <FormHelperText>
                    {expenseFundingType === 'investor_outofpocket'
                      ? 'Counts as capital in the Investment Tracker. Never repaid.'
                      : 'Paid with your own card or PayLater and want it back? Add it to your liquidation instead.'}
                  </FormHelperText>
                </FormControl>
              </Grid>
              {expenseFundingType === 'investor_outofpocket' && (
                <Grid size={{ xs: 6 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Investor</InputLabel>
                    <Select label="Investor" value={expenseFundingInvestor} onChange={(e) => handleExpenseInvestorChange(e.target.value)}>
                      <MenuItem value="">— Select investor —</MenuItem>
                      {INVESTORS.map((inv) => <MenuItem key={inv} value={inv}>{inv}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {expenseFundingType === 'investor_outofpocket' && expenseFundingInvestor && expenseLinkedInvestments.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Link to Existing Investment (Optional)</InputLabel>
                    <Select label="Link to Existing Investment (Optional)" value={expenseLinkedInvestmentId} onChange={(e) => setExpenseLinkedInvestmentId(e.target.value)}>
                      <MenuItem value="">— New investment entry —</MenuItem>
                      {expenseLinkedInvestments.map((inv) => (
                        <MenuItem key={inv.id} value={inv.id}>
                          {inv.date} · {inv.category} · {inv.description || '—'} · {formatCurrency(inv.amount)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {expenseDuplicateWarnings.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning" variant="outlined">
                    Possible duplicate receipt
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {expenseDuplicateWarnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </Alert>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <Accordion variant="outlined" disableGutters sx={{ mt: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" color="text.secondary">BIR Substantiation Details (Optional)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField fullWidth size="small" label="Supplier / Vendor Name" value={expenseSupplier} onChange={(e) => setExpenseSupplier(e.target.value)} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth size="small" label="Supplier TIN" placeholder="000-000-000-00000" value={expenseTin} onChange={(e) => setExpenseTin(e.target.value)} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Document Type</InputLabel>
                          <Select label="Document Type" value={expenseInvoiceType} onChange={(e) => setExpenseInvoiceType(e.target.value)}>
                            <MenuItem value="">— None —</MenuItem>
                            {INVOICE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth size="small" label="Invoice / OR Number" value={expenseInvoiceNo} onChange={(e) => setExpenseInvoiceNo(e.target.value)} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth size="small" label="Input VAT (supplier)" type="number" value={expenseVat} onChange={(e) => setExpenseVat(e.target.value)} inputProps={{ min: 0, step: 0.01 }} />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              size="small"
              color="primary"
              startIcon={isScanning ? <CircularProgress size={18} /> : <PhotoCameraIcon />}
              onClick={() => scanInputRef.current?.click()}
              disabled={isScanning}
            >
              Scan One
            </Button>
            <Button
              size="small"
              color="primary"
              startIcon={<PhotoLibraryIcon />}
              onClick={() => setScanBatchOpen(true)}
            >
              Scan Multiple
            </Button>
          </Box>
          <Box>
            <Button onClick={() => { setAddExpenseOpen(false); setExpenseImageHash(null); setExpenseDuplicateWarnings([]); }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAddExpense}
              disabled={isScanning || savingExpense}
              sx={{ ml: 1, backgroundColor: NET_PACIFIC_COLORS.primary, '&:hover': { backgroundColor: NET_PACIFIC_COLORS.secondary } }}
            >
              Add Expense
            </Button>
          </Box>
        </DialogActions>
        <input
          type="file"
          ref={scanInputRef}
          accept={RECEIPT_FILE_ACCEPT}
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleScanInputChange}
        />
      </Dialog>

      {/* Crop step between photo pick and AI parse (auto-detected corners, adjustable) */}
      <Dialog open={!!editUrl} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust Receipt Crop</DialogTitle>
        <DialogContent>
          {editUrl && editQuad && (
            <ReceiptCropper imageUrl={editUrl} initialQuad={editQuad} busy={isScanning} onConfirm={confirmScanCrop} onRetake={retakeScan} />
          )}
        </DialogContent>
      </Dialog>

      {/* Multi-upload: pick several receipt photos, crop/parse each, save all at once.
          Each receipt gets its own Project picker in review, so one batch can mix
          receipts from different projects — it defaults to whatever project (if any)
          is already selected in the Add Expense dialog above. */}
      <Dialog open={scanBatchOpen} onClose={() => setScanBatchOpen(false)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>Scan Multiple Receipts</DialogTitle>
        <DialogContent>
          {scanBatchOpen && (() => {
            const project = allProjects.find((p) => String(p.id) === expenseProjectId);
            const scanProjects = allProjects.map((p) => ({ id: String(p.id), project_no: p.project_no || String(p.id), project_name: p.project_name, account_name: p.account_name }));
            return (
              <ScanBatch
                mode="project"
                selectedProject={project ? { id: String(project.id), project_no: project.project_no || String(project.id), project_name: project.project_name, account_name: project.account_name } : null}
                projects={scanProjects}
                categories={PROJECT_EXPENSE_CATEGORIES}
                onCancel={() => setScanBatchOpen(false)}
                onComplete={(n) => {
                  setScanBatchOpen(false);
                  setAddExpenseOpen(false);
                  setScanSnackbar({ open: true, severity: 'success', message: `Saved ${n} receipt${n === 1 ? '' : 's'}.` });
                  fetchExpenses();
                }}
              />
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Superadmin: promote a manual/scanned expense to an employee's liquidation claim */}
      <Dialog open={!!promoteExpense} onClose={() => setPromoteExpense(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Promote to Liquidation</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {promoteExpense && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {promoteExpense.description || promoteExpense.category} — {formatCurrency(promoteExpense.amount)} on {promoteExpense.date}.
                This will remove it from Expense Monitoring, carry over its attached receipt, and{' '}
                {promoteTargetLiquidationId
                  ? `add it as a new row on the ${promoteDrafts.find((d) => d.id === promoteTargetLiquidationId)?.status === 'submitted' ? 'submitted' : 'draft'} liquidation selected below.`
                  : 'create a new submitted liquidation for the employee below.'}
              </Typography>
            )}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Employee</InputLabel>
                  <Select label="Employee" value={promoteUserId} onChange={(e) => handlePromoteUserChange(e.target.value)}>
                    <MenuItem value="">— Select employee —</MenuItem>
                    {promoteUsers.map((u) => (
                      <MenuItem key={u.id} value={String(u.id)}>{u.full_name || u.username}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small" disabled={!promoteUserId}>
                  <InputLabel>Add to</InputLabel>
                  <Select
                    label="Add to"
                    value={promoteTargetLiquidationId}
                    onChange={(e) => { setPromoteTargetLiquidationId(e.target.value); setPromoteCaId(''); }}
                  >
                    <MenuItem value="">New liquidation (submitted immediately)</MenuItem>
                    {promoteDrafts.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.form_no} — {d.status === 'draft' ? 'draft' : 'submitted'}, {formatCurrency(d.total_amount)} so far
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {!promoteTargetLiquidationId && (
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth size="small" disabled={!promoteUserId}>
                    <InputLabel>Cash Advance</InputLabel>
                    <Select label="Cash Advance" value={promoteCaId} onChange={(e) => setPromoteCaId(e.target.value)}>
                      <MenuItem value="">Standalone (Out-of-Pocket reimbursement)</MenuItem>
                      {promoteCAs.map((ca) => (
                        <MenuItem key={ca.id} value={ca.id}>{ca.ca_no} — balance {formatCurrency(ca.balance_remaining)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {promoteError && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="error">{promoteError}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPromoteExpense(null)} disabled={promoting}>Cancel</Button>
          <Button variant="contained" onClick={handlePromote} disabled={promoting || !promoteUserId}>
            {promoting ? 'Promoting…' : 'Promote'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View expenses per project dialog */}
      <Dialog open={!!expensesDialogProject} onClose={() => setExpensesDialogProject(null)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', pb: 1 }}>
          Expenses – {expensesDialogProject?.project_name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {expensesDialogProject && (
            <>
              {expensesForProject(String(expensesDialogProject.id)).length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>No expenses for this project yet.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expensesForProject(String(expensesDialogProject.id)).map((e) => (
                        <TableRow key={e.id}>
                          <TableCell>{e.date}</TableCell>
                          <TableCell>{e.description}</TableCell>
                          <TableCell>{e.category}</TableCell>
                          <TableCell align="right">{formatCurrency(e.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e2e8f0', p: 2 }}>
          <Button onClick={() => setExpensesDialogProject(null)}>Close</Button>
          {expensesDialogProject && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setExpenseScope('project');
                setExpenseProjectId(String(expensesDialogProject.id));
                setExpenseAmount('');
                setExpenseDate(new Date().toISOString().slice(0, 10));
                setExpenseDescription('');
                setExpenseRemarks('');
                setExpenseCategory('');
                setExpenseSupplier('');
                setExpenseInvoiceNo('');
                setExpenseInvoiceType('');
                setExpenseVat('');
                setExpenseTin('');
                setExpenseImageHash(null);
                setExpenseDuplicateWarnings([]);
                setAddExpenseOpen(true);
                setExpensesDialogProject(null);
              }}
            >
              Add expense
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit an existing expense row (scope-aware PATCH) */}
      <Dialog open={!!editExpense} onClose={() => !savingEdit && setEditExpense(null)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>
          Edit {editExpense?.scope === 'overhead' ? 'Overhead' : 'Project'} Expense
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Amount" type="number" value={editFields.amount}
                  onChange={(e) => setEditFields((f) => ({ ...f, amount: e.target.value }))} inputProps={{ min: 0, step: 0.01 }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Date" type="date" value={editFields.date}
                  onChange={(e) => setEditFields((f) => ({ ...f, date: e.target.value }))} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth size="small" label="Description" value={editFields.description}
                  onChange={(e) => setEditFields((f) => ({ ...f, description: e.target.value }))} multiline rows={2} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth size="small" label="Remarks" value={editFields.remarks}
                  onChange={(e) => setEditFields((f) => ({ ...f, remarks: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select label="Category" value={editFields.category} onChange={(e) => setEditFields((f) => ({ ...f, category: e.target.value }))}>
                    <MenuItem value="">— Select category —</MenuItem>
                    {(editExpense?.scope === 'overhead' ? OVERHEAD_CATEGORIES : PROJECT_EXPENSE_CATEGORIES).map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                    {/* Keep a legacy/free-text category selectable so opening the dialog doesn't silently drop it */}
                    {editFields.category && !((editExpense?.scope === 'overhead' ? OVERHEAD_CATEGORIES : PROJECT_EXPENSE_CATEGORIES) as readonly string[]).includes(editFields.category) && (
                      <MenuItem value={editFields.category}>{editFields.category}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Accordion variant="outlined" disableGutters sx={{ mt: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" color="text.secondary">BIR Substantiation Details (Optional)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField fullWidth size="small" label="Supplier / Vendor Name" value={editFields.supplier}
                          onChange={(e) => setEditFields((f) => ({ ...f, supplier: e.target.value }))} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth size="small" label="Supplier TIN" placeholder="000-000-000-00000" value={editFields.tin}
                          onChange={(e) => setEditFields((f) => ({ ...f, tin: e.target.value }))} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Document Type</InputLabel>
                          <Select label="Document Type" value={editFields.invoiceType} onChange={(e) => setEditFields((f) => ({ ...f, invoiceType: e.target.value }))}>
                            <MenuItem value="">— None —</MenuItem>
                            {INVOICE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth size="small" label="Invoice / OR Number" value={editFields.invoiceNo}
                          onChange={(e) => setEditFields((f) => ({ ...f, invoiceNo: e.target.value }))} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth size="small" label="Input VAT (supplier)" type="number" value={editFields.vat}
                          onChange={(e) => setEditFields((f) => ({ ...f, vat: e.target.value }))} inputProps={{ min: 0, step: 0.01 }} />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
              {editError && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="error">{editError}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditExpense(null)} disabled={savingEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={savingEdit}
            sx={{ backgroundColor: NET_PACIFIC_COLORS.primary, '&:hover': { backgroundColor: NET_PACIFIC_COLORS.secondary } }}>
            {savingEdit ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move a row between overhead and a project (server-side copy + delete) */}
      <Dialog open={!!moveExpense} onClose={() => !moving && setMoveExpense(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {moveExpense?.scope === 'overhead' ? 'Move to Project Expenses' : 'Move to Overhead Expenses'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {moveExpense && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {moveExpense.description || moveExpense.category} — {formatCurrency(moveExpense.amount)} on {moveExpense.date}.
                {moveExpense.scope === 'overhead'
                  ? ' This will file it under the selected project (COGS) instead of company overhead.'
                  : ' This will detach it from its project and file it as company overhead (OPEX).'}
              </Typography>
            )}
            <Grid container spacing={2}>
              {moveExpense?.scope === 'overhead' && (
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Project</InputLabel>
                    <Select label="Project" value={moveProjectId} onChange={(e) => setMoveProjectId(String(e.target.value))}>
                      <MenuItem value="">— Select project —</MenuItem>
                      {allProjects.map((p) => (
                        <MenuItem key={p.id} value={String(p.id)}>{p.project_name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category (target)</InputLabel>
                  <Select label="Category (target)" value={moveCategory} onChange={(e) => setMoveCategory(e.target.value)}>
                    <MenuItem value="">— Keep as "{moveExpense?.category || 'Others'}" —</MenuItem>
                    {(moveExpense?.scope === 'overhead' ? PROJECT_EXPENSE_CATEGORIES : OVERHEAD_CATEGORIES).map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {moveError && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="error">{moveError}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMoveExpense(null)} disabled={moving}>Cancel</Button>
          <Button variant="contained" onClick={handleMove} disabled={moving || (moveExpense?.scope === 'overhead' && !moveProjectId)}>
            {moving ? 'Moving…' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating draggable receipt viewer pane */}
      {viewer && (
        <ReceiptViewer
          title={`${viewer.expense.description || viewer.expense.category} — ${formatCurrency(viewer.expense.amount)}`}
          url={viewer.url}
          webUrl={viewer.expense.receiptRef?.webUrl}
          isPdf={/\.pdf$/i.test(viewer.expense.receiptRef?.filename || '')}
          onClose={() => setViewer(null)}
          headerColor={NET_PACIFIC_COLORS.primary}
          onSaveRotation={viewer.expense.receiptRef?.oneDriveId
            ? (blob) => saveReceiptRotation(viewer.expense.receiptRef!.oneDriveId, blob)
            : undefined}
        />
      )}

      {/* Hidden picker for attach-receipt-later on existing table rows */}
      <input
        type="file"
        ref={attachReceiptInputRef}
        accept="image/*,.pdf,.heic,.heif"
        style={{ display: 'none' }}
        onChange={handleAttachReceiptInputChange}
      />

      <Snackbar
        open={scanSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setScanSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setScanSnackbar((p) => ({ ...p, open: false }))}
          severity={scanSnackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {scanSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExpenseMonitoring;
